/**
 * Kontroller per component: which building checks (`inspections/active/{b}/{checkKey}`)
 * are about a component. Used by the maintenance draft to show work that is already
 * handled by a check, so it is not planned again as maintenance. Nothing about
 * this is stored in the maintenance plan.
 *
 * A check is about at most one component, its `componentKey`. Skickbedömning
 * (assessment-group templates) is left out: it is scheduled per group in the
 * activation dialog, so a per-component row would only repeat it, and its date
 * may still change at activation.
 */

import type { BaseComponent, Building } from '../types/protocols';
import type { DateInterval } from './DateUtils';
import { isLiveComponent } from './assessmentGroups';
import { groupBy } from './groupBy';
import type { BuildingCheckTemplate } from '../Pages/BuildingChecks/types';
import type { BuildingCheck } from '../Pages/BuildingChecks/buildingChecksUtils';

export interface ComponentCheck {
    checkKey: string;
    templateId: string;
    template: BuildingCheckTemplate;
    instance: BuildingCheck;
    /** The component the check is about */
    componentKey: string;
    /** The check's display name ("Brandsläckare – Trapphus A") */
    name: string;
    /** The check's custom interval, else the template's */
    interval: DateInterval;
}

/**
 * One Kontroller row of the draft: the checks of one template on the shown
 * component(s). A single component has at most one check per template; in
 * Massredigering a row holds the template's checks on the selected components.
 */
export interface CheckRow {
    templateId: string;
    template: BuildingCheckTemplate;
    /** The check's name for one check, else the template name */
    name: string;
    checks: ComponentCheck[];
    /** Shared interval, or null when the checks differ */
    interval: DateInterval | null;
    /** Shared next due date, or null when the checks differ */
    nextDueDate: string | null;
}

type ComponentMap = Record<string, BaseComponent>;

/** Display name of a check, given its template's name */
export type CheckNamer = (check: BuildingCheck, templateName: string) => string;

export const componentsOfBuilding = (building: Building | null): ComponentMap =>
    (building?.model?.structure?.components as ComponentMap | undefined) ?? {};

/** Every check on the building about the component, by name; never Skickbedömning */
export const checksCoveringComponent = (
    componentKey: string,
    components: ComponentMap,
    instances: Record<string, BuildingCheck>,
    templates: Record<string, BuildingCheckTemplate>,
    nameOf: CheckNamer = (_check, templateName) => templateName
): ComponentCheck[] => {
    if (!isLiveComponent(components[componentKey])) return [];

    const result: ComponentCheck[] = [];
    for (const [checkKey, instance] of Object.entries(instances)) {
        if (instance.componentKey !== componentKey) continue;
        const template = templates[instance.templateId];
        if (!template || template.componentBaseTypeGroup) continue;
        result.push({
            checkKey,
            templateId: instance.templateId,
            template,
            instance,
            componentKey,
            name: nameOf(instance, template.name),
            interval: instance.customInterval ?? template.interval
        });
    }
    return result.sort((a, b) => a.name.localeCompare(b.name, 'sv'));
};

const sameInterval = (a: DateInterval, b: DateInterval) => a.value === b.value && a.unit === b.unit;

/** The checks grouped into one row per template, sorted by name */
export const checkRowsByTemplate = (checks: ComponentCheck[]): CheckRow[] => {
    return Array.from(groupBy(checks, check => check.templateId).values())
        .map((rowChecks): CheckRow => {
            const [first] = rowChecks;
            return {
                templateId: first.templateId,
                template: first.template,
                name: rowChecks.length === 1 ? first.name : first.template.name,
                checks: rowChecks,
                interval: rowChecks.every(check => sameInterval(check.interval, first.interval)) ? first.interval : null,
                nextDueDate: rowChecks.every(check => check.instance.nextDueDate === first.instance.nextDueDate)
                    ? first.instance.nextDueDate
                    : null
            };
        })
        .sort((a, b) => a.name.localeCompare(b.name, 'sv'));
};

/**
 * Templates a check can be created from for the component(s): every template but
 * Skickbedömning, except those whose checks already cover every one of them
 * (a component is in at most one check per template).
 */
export const linkableTemplates = (
    templates: Record<string, BuildingCheckTemplate>,
    coveringAll: Array<Pick<ComponentCheck, 'templateId'>>
): BuildingCheckTemplate[] => {
    const covered = new Set(coveringAll.map(check => check.templateId));
    return Object.values(templates)
        .filter(template => !template.componentBaseTypeGroup && !covered.has(template.id))
        .sort((a, b) => a.name.localeCompare(b.name, 'sv'));
};
